import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FaqDto, FaqPayloadVM } from "../../types/faq.definition";

interface FaqState {
  faqs: FaqDto[];
  totalCount: number;
  loadedLang: string | null;
  loading: boolean;
  saving: boolean;
  deleting: boolean;
  error: string | null;
}

const initialState: FaqState = {
  faqs: [],
  totalCount: 0,
  loadedLang: null,
  loading: false,
  saving: false,
  deleting: false,
  error: null,
};

// Actions
export const fetchFaqsRequest = createAction<{
  lang?: string;
}>("faqs/fetchFaqsRequest");

export const fetchFaqsSuccess = createAction<{
  data: FaqDto[];
  totalCount: number;
  lang: string;
}>("faqs/fetchFaqsSuccess");

export const fetchFaqsFailure = createAction<string>("faqs/fetchFaqsFailure");

export const createFaqRequest = createAction<{
  data: FaqPayloadVM;
  onSuccess?: () => void;
}>("faqs/createFaqRequest");

export const createFaqSuccess = createAction<FaqDto>("faqs/createFaqSuccess");
export const createFaqFailure = createAction<string>("faqs/createFaqFailure");

export const updateFaqRequest = createAction<{
  id: number;
  data: FaqPayloadVM;
  onSuccess?: () => void;
}>("faqs/updateFaqRequest");

export const updateFaqSuccess = createAction<FaqDto>("faqs/updateFaqSuccess");
export const updateFaqFailure = createAction<string>("faqs/updateFaqFailure");

export const deleteFaqRequest = createAction<{
  id: number;
  onSuccess?: () => void;
  onError?: (code: string) => void;
}>("faqs/deleteFaqRequest");

export const deleteFaqSuccess = createAction<number>("faqs/deleteFaqSuccess");
export const deleteFaqFailure = createAction<string>("faqs/deleteFaqFailure");

// Slice
const faqSlice = createSlice({
  name: "faqs",
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch
    builder
      .addCase(fetchFaqsRequest, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchFaqsSuccess,
        (
          state,
          action: PayloadAction<{
            data: FaqDto[];
            totalCount: number;
            lang: string;
          }>
        ) => {
          state.loading = false;
          state.faqs = action.payload.data;
          state.totalCount = action.payload.totalCount;
          state.loadedLang = action.payload.lang;
          state.error = null;
        }
      )
      .addCase(fetchFaqsFailure, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create
    builder
      .addCase(createFaqRequest, state => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createFaqSuccess, state => {
        state.saving = false;
        state.error = null;
      })
      .addCase(createFaqFailure, (state, action: PayloadAction<string>) => {
        state.saving = false;
        state.error = action.payload;
      });

    // Update
    builder
      .addCase(updateFaqRequest, state => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateFaqSuccess, state => {
        state.saving = false;
        state.error = null;
      })
      .addCase(updateFaqFailure, (state, action: PayloadAction<string>) => {
        state.saving = false;
        state.error = action.payload;
      });

    // Delete
    builder
      .addCase(deleteFaqRequest, state => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteFaqSuccess, (state, action: PayloadAction<number>) => {
        state.deleting = false;
        state.faqs = state.faqs.filter(faq => faq.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.error = null;
      })
      .addCase(deleteFaqFailure, (state, action: PayloadAction<string>) => {
        state.deleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = faqSlice.actions;

// Selectors
export const selectFaqs = (state: { faqs: FaqState }) => state.faqs.faqs;
export const selectFaqsTotalCount = (state: { faqs: FaqState }) =>
  state.faqs.totalCount;
export const selectFaqsLoadedLang = (state: { faqs: FaqState }) =>
  state.faqs.loadedLang;
export const selectFaqsLoading = (state: { faqs: FaqState }) =>
  state.faqs.loading;
export const selectFaqsSaving = (state: { faqs: FaqState }) =>
  state.faqs.saving;
export const selectFaqsDeleting = (state: { faqs: FaqState }) =>
  state.faqs.deleting;
export const selectFaqsError = (state: { faqs: FaqState }) => state.faqs.error;

export default faqSlice.reducer;
